import { useEffect, useState } from 'react';
import type { PasswordCheck } from '@hoopify/shared/password-lite';
import { Field } from './ui';

type Checker = (password: string, inputs?: string[]) => PasswordCheck;
let checker: Promise<Checker> | null = null;
const loadChecker = () => (checker ??= import('@hoopify/shared/password-lite').then((m) => m.checkPassword));

const LABELS = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];

/** New-password input with a live strength meter, using the same rules as the server. */
export function NewPasswordField({
  value,
  onChange,
  error,
  userInputs,
  label = 'Password',
  name = 'password',
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
  userInputs: string[];
  label?: string;
  name?: string;
}) {
  const [check, setCheck] = useState<PasswordCheck | null>(null);
  const inputsKey = userInputs.join('|');

  useEffect(() => {
    let live = true;
    if (!value) {
      setCheck(null);
      return;
    }
    const t = setTimeout(() => {
      void loadChecker().then((fn) => live && setCheck(fn(value, userInputs)));
    }, 120);
    return () => {
      live = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inputsKey]);

  const score = check?.score ?? 0;
  const tone = !check ? 'bg-line' : check.ok ? 'bg-ok' : score >= 2 ? 'bg-warn' : 'bg-danger';

  return (
    <div className="space-y-2">
      <Field
        label={label}
        name={name}
        type="password"
        autoComplete="new-password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={error}
        hint="At least 10 characters. A few unrelated words strung together work well."
        required
      />
      {value && (
        <div aria-live="polite">
          <div className="flex gap-1" aria-hidden>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`h-1 flex-1 rounded-full ${check && (check.ok ? i <= score - 1 : i < Math.max(1, score)) ? tone : 'bg-line'}`} />
            ))}
          </div>
          {check && (
            <p className={`mt-1.5 text-[13px] ${check.ok ? 'text-ok' : 'text-muted'}`}>
              {check.ok ? `${LABELS[check.score]} password.` : check.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
